import { useQuery } from '@tanstack/react-query'
import { Button, Modal, SpinnerWithBackground, Text } from '@viapeople/components'
import { trpc } from '../../../../utils/trpc'
import type { AiSummaryConfig } from '../../types'

interface AiSummaryStatusModalProps {
	isOpen: boolean
	onClose: () => void
	config: AiSummaryConfig | null
	cycleId: string
}

export const AiSummaryStatusModal = ({ isOpen, onClose, config, cycleId }: AiSummaryStatusModalProps) => {
	const { data: stats, isLoading } = useQuery({
		...trpc.admin.aiSummaries.configs.stats.queryOptions({
			cycleId,
			configId: config?.id || '',
		}),
		enabled: !!config && isOpen,
	})

	if (!config) return null

	const completionRate = stats
		? stats.totalEvaluations > 0
			? Math.round((stats.generated / stats.totalEvaluations) * 100)
			: 0
		: 0

	return (
		<Modal isOpen={isOpen} onClose={onClose} title={`Status: ${config.label}`}>
			{isLoading ? (
				<div className='flex items-center justify-center py-12'>
					<SpinnerWithBackground />
				</div>
			) : stats ? (
				<div className='space-y-6'>
					<div className='grid grid-cols-2 gap-4'>
						<div className='bg-neutral-50 rounded-lg p-4'>
							<Text variant='small' className='text-neutral-600 mb-1'>
								Total Evaluations
							</Text>
							<Text variant='large' weight='semibold'>
								{stats.totalEvaluations}
							</Text>
						</div>

						<div className='bg-green-50 rounded-lg p-4'>
							<Text variant='small' className='text-green-700 mb-1'>
								Generated
							</Text>
							<Text variant='large' weight='semibold' className='text-green-700'>
								{stats.generated}
							</Text>
						</div>

						<div className='bg-yellow-50 rounded-lg p-4'>
							<Text variant='small' className='text-yellow-700 mb-1'>
								Missing
							</Text>
							<Text variant='large' weight='semibold' className='text-yellow-700'>
								{stats.missing}
							</Text>
						</div>

						{stats.pending > 0 && (
							<div className='bg-blue-50 rounded-lg p-4'>
								<Text variant='small' className='text-blue-700 mb-1'>
									Pending Regeneration
								</Text>
								<Text variant='large' weight='semibold' className='text-blue-700'>
									{stats.pending}
								</Text>
							</div>
						)}
					</div>

					<div>
						<Text variant='small' className='text-neutral-600 mb-2'>
							Completion Rate
						</Text>
						<div className='w-full bg-neutral-200 rounded-full h-4'>
							<div
								className='bg-primary-600 h-4 rounded-full transition-all duration-300'
								style={{ width: `${completionRate}%` }}
							/>
						</div>
						<Text variant='small' className='text-neutral-600 mt-1'>
							{completionRate}% complete
						</Text>
					</div>

					{stats.lastGeneratedAt && (
						<div>
							<Text variant='small' className='text-neutral-600'>
								Last Generated
							</Text>
							<Text variant='medium' weight='semibold'>
								{new Date(stats.lastGeneratedAt).toLocaleString()}
							</Text>
						</div>
					)}
				</div>
			) : (
				<Text>No stats available</Text>
			)}

			<div className='flex justify-end mt-6'>
				<Button onClick={onClose}>Close</Button>
			</div>
		</Modal>
	)
}
